import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Document } from '../types/Document';

interface DocumentCardProps {
  document: Document;
  showPreview?: boolean;
  onClick?: () => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ 
  document, 
  showPreview = true,
  onClick 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className="document-item"
      onClick={handleCardClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="document-image-container" style={{ 
        position: 'relative',
        height: '200px',
        backgroundColor: 'var(--democracy-silver)',
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {!imageError ? (
          <>
            <img
              src={document.thumbnailPath}
              alt={`Document ${document.filename}`}
              className="document-image"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            />
            {!imageLoaded && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(26, 31, 58, 0.5)',
                fontSize: 'var(--font-size-sm)'
              }}>
                Loading...
              </div>
            )}
          </>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-xs)',
            color: 'rgba(26, 31, 58, 0.6)'
          }}>
            <div style={{ fontSize: '2rem' }}>📄</div>
            <div style={{ fontSize: 'var(--font-size-sm)', textAlign: 'center' }}>
              Image Preview<br/>Not Available
            </div>
          </div>
        )}
      </div>

      <div className="document-info">
        <h3 className="document-title">
          {document.filename}
        </h3>
        
        <div style={{ 
          display: 'flex', 
          gap: 'var(--space-xs)',
          alignItems: 'center',
          marginBottom: 'var(--space-sm)',
          fontSize: 'var(--font-size-sm)',
          color: 'rgba(26, 31, 58, 0.6)'
        }}>
          <span>📁 Folder {document.folder}</span>
          <span>•</span>
          <span>🆔 {document.id}</span>
        </div>

        {showPreview && document.extractedText && (
          <div className="document-preview">
            {document.extractedText.substring(0, 150)}
            {document.extractedText.length > 150 && '...'}
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          gap: 'var(--space-xs)',
          marginTop: 'var(--space-md)',
          flexWrap: 'wrap'
        }}>
          <Link 
            to={`/document/${document.folder}/${document.id}`} 
            className="btn"
            style={{ 
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-xs) var(--space-sm)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            View Full
          </Link>
          
          <a
            href={document.imagePath}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ 
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-xs) var(--space-sm)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            Image
          </a>
          
          <a
            href={document.textPath}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ 
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-xs) var(--space-sm)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            Text
          </a>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;